import { FILTER_FIELD_GROUPS, QUICK_FILTER_KEYS } from "@/config/filterFields";

/**
 * The filter bar's per-browser conveniences (owner, 30.10.2026): the last few
 * filter sets, and which fields sit in the bar as empty quick slots. Kept in
 * localStorage, which can be missing or throw (a private window, blocked site
 * data) - then the bar runs on the defaults and remembers nothing.
 */

/** A filter set as the URL holds it: `filters` without the bot preset, and `where`. */
export interface RecentFilters {
  filters: string;
  where: string;
}

export const RECENT_MAX = 5;
export const PINNED_MAX = 5;
const RECENT_KEY = "oracul.clicks.recentFilters";
const PINNED_KEY = "oracul.clicks.quickSlots";

type Store = Pick<Storage, "getItem" | "setItem">;
const browserStore = (): Store | null => {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
};

const isRecent = (x: unknown): x is RecentFilters =>
  !!x && typeof (x as RecentFilters).filters === "string" && typeof (x as RecentFilters).where === "string";

export function readRecent(store: Store | null = browserStore()): RecentFilters[] {
  try {
    const raw: unknown = JSON.parse(store?.getItem(RECENT_KEY) ?? "[]");
    return Array.isArray(raw) ? raw.filter(isRecent).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

/** Newest first, no repeats, at most RECENT_MAX. No filters is not a set worth keeping. */
export function withRecent(list: RecentFilters[], entry: RecentFilters): RecentFilters[] {
  if (!entry.filters && !entry.where) return list;
  return [entry, ...list.filter((e) => e.filters !== entry.filters || e.where !== entry.where)].slice(0, RECENT_MAX);
}

export function writeRecent(list: RecentFilters[], store: Store | null = browserStore()): void {
  try {
    store?.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    // Full or blocked: the bar just forgets.
  }
}

const FIELD_KEYS = new Set(FILTER_FIELD_GROUPS.flatMap((g) => g.fields.map((f) => f.key)));

/** The quick-slot fields; Brand, Country, Campaign until someone pins their own. */
export function readPinned(store: Store | null = browserStore()): string[] {
  try {
    const raw = store?.getItem(PINNED_KEY);
    if (raw == null) return [...QUICK_FILTER_KEYS];
    const list: unknown = JSON.parse(raw);
    if (!Array.isArray(list)) return [...QUICK_FILTER_KEYS];
    return [...new Set(list.filter((k): k is string => typeof k === "string" && FIELD_KEYS.has(k)))].slice(0, PINNED_MAX);
  } catch {
    return [...QUICK_FILTER_KEYS];
  }
}

/** Pin or unpin a field; a full bar takes no more. */
export const togglePinned = (list: string[], key: string): string[] =>
  list.includes(key) ? list.filter((k) => k !== key) : list.length >= PINNED_MAX ? list : [...list, key];

export function writePinned(list: string[], store: Store | null = browserStore()): void {
  try {
    store?.setItem(PINNED_KEY, JSON.stringify(list));
  } catch {
    // Full or blocked: back to the defaults next time.
  }
}
